import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useI18n } from '@/i18n';

// Utils
import cn from '@/utils/cn';
import { buildLocalizedPath } from '@/utils/localeSlug';

// Components
import Button from '@/components/atoms/Button/Button';

// Other 
import { type HeaderType } from '@/services/header/headerService';
import { type MediaType } from '@/services/strapiSchema';
import ChevronDownDarkIcon from '@/assets/icons/chevron-down-dark.svg?react';
import ChevronDownLightIcon from '@/assets/icons/chevron-down-light.svg?react';
import LanguageIcon from '@/assets/icons/language.svg?react';
import type { resources } from '@/locales/resources';
import { getLocaleFlag, supportedLocales } from '@/utils/localeFlags';

// Styles
import styles from './Menu.module.css';

interface MenuProps{
  logo: MediaType,
  menu: HeaderType['menu'],
  loginButton: HeaderType['loginButton'],
  registerButton: HeaderType['registerButton'],
  color: 'dark' | 'light',
}

function Menu({
  logo,
  menu,
  loginButton,
  registerButton,
  color,
}: MenuProps) {
  const { t } = useTranslation();
  const { getLng, changeLng, localizedPath } = useI18n();
  const [dropdownStates, setDropdownStates] = useState<{[key: number]: boolean}>({});
  const [dropdownLanguage, setDropdownLanguage] = useState(false);

  const toggleDropdown = (index: number) => {
    setDropdownStates(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const handleLanguageChange = (lng: keyof typeof resources) => {
    changeLng(lng);
    setDropdownLanguage(false);
  };

  return (
    <>
      <Link to={buildLocalizedPath('', getLng())}>
        <img src={logo.url} alt={logo.alternativeText || 'Pneumaclic logo'} />
      </Link>
      <nav className={styles.nav}>
        {
          menu.map((item, index) => (
            item.linkType === 'Link' ? (
              <Link key={item.link.title} to={localizedPath(item.link.url)} className={styles['nav-item']}>
                {item.link.title}
              </Link>
            ) : (
              <div key={item.link.title} className={styles.dropdown}>
                <Link to={localizedPath(item.link.url)} className={styles['nav-item']}>
                  {item.link.title}
                </Link>
                <button
                  type="button"
                  onClick={() => toggleDropdown(index)}
                  className={`${styles['dropdown-icon']} ${dropdownStates[index] && styles.open}`}
                >
                  {
                    color === 'light' ? (
                      <ChevronDownDarkIcon />
                    ) : (
                      <ChevronDownLightIcon />
                    )
                  }
                </button>
                {dropdownStates[index] && item.dropdown && (
                  <div
                    className={cn([
                      styles['dropdown-content'],
                      styles[`dropdown-content-${color}`]
                    ])}
                  >
                    {
                      item.dropdown.map((link) => (
                        <Link key={link.title} to={localizedPath(link.url)} className={styles['nav-item']}>
                          {link.title}
                        </Link>
                      ))
                    }
                  </div>
                )}
              </div>
            )
          ))
        }
        <div className={styles.line} />
      </nav>
      <div className={styles.buttons}>
        <Button
          title={loginButton.title}
          bgColor='white'
          url={loginButton.url}
        />
        <Button
          title={registerButton.title}
          bgColor='red'
          url={registerButton.url}
        />
      </div>
      <div className={styles.dropdown}>
        <button
          type='button'
          onClick={() => setDropdownLanguage(!dropdownLanguage)}
        >
          <LanguageIcon
            width={25}
            height={25}
          />
        </button>
        {
          dropdownLanguage && (
            <div
              className={cn([
                styles['dropdown-content'],
                styles[`dropdown-content-${color}`],
                styles['dropdown-content-open'],
              ])}
            >
              {supportedLocales.map((locale) => {
                const FlagIcon = getLocaleFlag(locale);

                return (
                  <button
                    key={locale}
                    type='button'
                    className={styles.flag}
                    onClick={() => handleLanguageChange(locale)}
                  >
                    <FlagIcon className={styles['flag-icon']} />
                    {t(`languages.${locale}`)}
                  </button>
                );
              })}
            </div>
          )
        }
      </div>
    </>
  );
}

export default Menu;